/* eslint-disable react-hooks/exhaustive-deps */
import CustomPagination from "components/CustomPagination";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { fetchCaseCreationByCRNumberAndIncidentRegNo } from "services/caseServices";
import {
  CaseCreation,
  CaseStatus,
  CaseStatusFilter,
  InvestigationFile,
  Roles,
} from "types";
import { actionListIcon } from "utils/constant";
import { DateView, errorHandler, hasRole } from "utils/helper";
import ClouserForm from "./modal/ClouserForm";
import PanelForm from "./modal/PanelForm";
import PerformanceForm from "./modal/PerformanceForm";
import ReAssignProsecutorForm from "./modal/ReAssignProsecutor";
import TerminationForm from "./modal/TerminationForm";
import ChangeOfVenueForm from "./modal/ChangeOfVenueForm";
import ArticleOfChangeForm from "./modal/ArticleOfChangeForm";
import SplitCases from "components/SplitCases";
import AppealRequestForm from "./modal/AppealRequestForm";
import { useTranslation } from "react-i18next";

const tabs = ["all", "merged", "split", "terminated"];
const ITEMS_PER_PAGE = 9;

// Later lifecycle stages are tracked on status_filter — once a case reaches
// the court, the meaningful label is the stage (e.g. "On Court Hearing"),
// not the internal workflow status (e.g. "sent_to_court").
const stageFilters = ["on_court_hearing", "final_verdict_given", "appealed"];

const getStatusColor = (
  status?: string | null,
  isCaseUpdatedByCourt?: boolean,
  statusFilter?: string | null,
) => {
  if (isCaseUpdatedByCourt) {
    return "bg-amber-100 text-amber-800";
  }

  if (statusFilter && stageFilters.includes(statusFilter)) {
    const filterColorMap: { [key: string]: string } = {
      on_court_hearing: "bg-green-100 text-green-800",
      final_verdict_given: "bg-indigo-100 text-indigo-800",
      appealed: "bg-purple-100 text-purple-800",
    };
    return filterColorMap[statusFilter] || "bg-gray-100 text-gray-800";
  }

  const statusMap: { [key: string]: string } = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    respond_back_cordinator: "bg-blue-100 text-blue-800",
    investigation_recorded: "bg-gray-100 text-gray-800",
    respond_back_prosecutor: "bg-purple-100 text-purple-800",
    forwarded_to_director: "bg-pink-100 text-pink-800",
    forwarded_to_coordinator: "bg-indigo-100 text-indigo-800",
    forwarded_to_registrar: "bg-orange-100 text-orange-800",
    sent_to_court: "bg-green-100 text-green-800",
    on_panel: "bg-blue-100 text-blue-800",
    case_terminated_by_court: "bg-red-100 text-red-800",
    out_of_jurisdiction: "bg-red-100 text-red-800",
  };

  return statusMap[(status || "").toLowerCase()] || "bg-gray-100 text-gray-800";
};

// Appeal must be requested within 15 days of the final verdict submission.
const APPEAL_WINDOW_DAYS = 15;
const DAY_MS = 24 * 60 * 60 * 1000;

// Deadline = (latest final_court_decision document created_at) + 15 days,
// falling back to the case created_at if the document isn't present.
const getAppealDeadline = (item: CaseCreation): number | null => {
  const docs = (item?.documents || []).filter(
    (d) => d?.document_type === "final_court_decision" && d?.created_at,
  );
  const base = docs.length
    ? Math.max(...docs.map((d) => new Date(d.created_at as any).getTime()))
    : (item as any)?.created_at
      ? new Date((item as any).created_at).getTime()
      : null;
  return base ? base + APPEAL_WINDOW_DAYS * DAY_MS : null;
};

type Props = {
  file: InvestigationFile;
  crimeReferenceNumber?: string;
  incidentRegistrationNumber?: string;
  setSelectedCaseId: React.Dispatch<React.SetStateAction<string | number>>;
  setPageType: React.Dispatch<React.SetStateAction<"list" | "detail">>;
};

const InstituteOfChargeList: React.FC<Props> = ({
  file,
  crimeReferenceNumber,
  incidentRegistrationNumber,
  setSelectedCaseId,
  setPageType,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [cases, setCases] = useState<CaseCreation[]>([]);
  const [loading, setLoading] = useState(true);
  // Ticks every minute to keep the appeal countdown badge current.
  const [now, setNow] = useState(Date.now());
  const [caseTooltip, setCaseTooltip] = React.useState<CaseCreation>();
  const [selectedCase, setSelectedCase] = React.useState<CaseCreation>();
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [openTerminationForm, setOpenTerminationForm] = React.useState(false);
  const [openPerformanceForm, setOpenPerformanceForm] = React.useState(false);
  const [openClouserForm, setOpenClouserForm] = React.useState(false);
  const [openChangeOfVenue, setOpenChangeOfVenue] = React.useState(false);
  const [openArticleOfChange, setOpenArticleOfChange] = React.useState(false);
  const [openSplitCase, setOpenSplitCase] = React.useState(false);
  const [openPanelForm, setOpenPanelForm] = React.useState(false);
  const [openReassignForm, setOpenReassignForm] = React.useState(false);
  const [openAppealForm, setOpenAppealForm] = React.useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const fetchData = () => {
    setLoading(true);
    fetchCaseCreationByCRNumberAndIncidentRegNo(
      crimeReferenceNumber ?? "",
      incidentRegistrationNumber ?? "",
    )
      .then((data) => {
        setCases(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const pageCount = Math.ceil(cases.length / ITEMS_PER_PAGE);
  const paginated = cases.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("case_list")}
          </h3>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(0);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                activeTab === tab
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {t(`institute_of_charge_tab_${tab}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
            {t("no_cases_found")}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-4 transition-all duration-150 hover:shadow-lg"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-red-600 text-base">
                        {t("reg_no")} {item?.prosecutor_registration_number}
                      </span>
                      <div className="flex items-center gap-2">
                        {item?.is_article_of_cahrge_changed && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-amber-100 text-amber-800 border border-amber-300">
                            ⚡ {t("article_changed")}
                          </span>
                        )}

                        {item?.is_RTD && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-green-50 text-green-700 border border-green-200">
                            🚀 {t("rtd_fast_decision")}
                          </span>
                        )}
                        {item?.is_case_out_of_jurisdiction &&
                          item?.status === "out_of_jurisdiction" && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-red-50 text-red-700 border border-red-200">
                              🗺️ {t("out_of_jurisdiction")}
                            </span>
                          )}
                        {item?.termination_status && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-700 border border-gray-300">
                            ⏹️ {t("terminated_label")}
                          </span>
                        )}
                        {item?.is_case_updated_by_court && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-blue-50 text-blue-700 border border-blue-200">
                            🔄 {t("case_updated")}
                          </span>
                        )}
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            item?.status,
                            item?.is_case_updated_by_court,
                            item?.status_filter,
                          )}`}
                        >
                          {item?.is_case_updated_by_court
                            ? // During a court update the case re-enters the
                              // workflow — show the real status, not the stale
                              // stage (the 🔄 badge marks it as updated).
                              item?.status
                              ? t(`status_${item.status}`, {
                                  defaultValue: CaseStatus[item.status],
                                })
                              : ""
                            : item?.status_filter &&
                                stageFilters.includes(item.status_filter)
                              ? t(`status_filter_${item.status_filter}`, {
                                  defaultValue:
                                    CaseStatusFilter[item.status_filter],
                                })
                              : item?.status
                                ? t(`status_${item.status}`, {
                                    defaultValue: CaseStatus[item.status],
                                  })
                                : ""}
                        </span>

                        {/* Appeal-request countdown (prosecutor only, once the
                            final verdict is submitted). */}
                        {isProsecutor &&
                          item?.status_filter === "final_verdict_given" &&
                          (() => {
                            const deadline = getAppealDeadline(item);
                            if (!deadline) return null;
                            const remainingDays = Math.ceil(
                              (deadline - now) / DAY_MS,
                            );
                            if (remainingDays > 0) {
                              const urgent = remainingDays <= 5;
                              return (
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border animate-pulse ${
                                    urgent
                                      ? "bg-red-50 text-red-700 border-red-200"
                                      : "bg-amber-50 text-amber-700 border-amber-200"
                                  }`}
                                  title={t("appeal_deadline_hint", {
                                    defaultValue:
                                      "Appeal must be requested within 15 days of the verdict",
                                  })}
                                >
                                  ⏳{" "}
                                  {t("appeal_days_left", {
                                    count: remainingDays,
                                    defaultValue:
                                      "{{count}} day(s) left to appeal",
                                  })}
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border bg-gray-100 text-gray-600 border-gray-300">
                                {t("appeal_period_ended", {
                                  defaultValue: "Appeal period ended",
                                })}
                              </span>
                            );
                          })()}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium">{t("date_sent")}:</span>
                        <span className="text-gray-900">
                          {DateView(item.date_sent_to_prosecutor)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium">{t("est_finish")}:</span>
                        <span className="text-gray-900">
                          {DateView(item.estimated_day_to_finish)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCaseId(item?.id);
                        setPageType("detail");
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      {t("view_details", { type: t("detail") })}
                    </button>

                    {(isProsecutor || isCoordinator) &&
                      !file?.is_case_suspended &&
                      !item?.is_article_of_cahrge_changed &&
                      item?.status !== "case_terminated_by_court" && (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              if (caseTooltip?.id === item.id) {
                                setCaseTooltip(undefined);
                                setDropdownPos(null);
                              } else {
                                setCaseTooltip(item);
                                const rect = (
                                  e.target as HTMLElement
                                ).getBoundingClientRect();
                                setDropdownPos({
                                  top: rect.bottom + window.scrollY,
                                  left: rect.left + window.scrollX,
                                });
                              }
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            {t("actions")}
                          </button>

                          {caseTooltip?.id === item.id && dropdownPos && (
                            <>
                              <div
                                className="fixed inset-0 bg-black bg-opacity-0 z-40"
                                onClick={() => {
                                  setCaseTooltip(undefined);
                                  setSelectedCase(undefined);
                                  setDropdownPos(null);
                                }}
                              />
                              <div
                                className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[240px] max-w-xs"
                                style={{
                                  top: dropdownPos.top + 8,
                                  left: dropdownPos.left,
                                }}
                              >
                                {isProsecutor && (
                                  <>
                                    {/* <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                      }}
                                    >
                                      {actionListIcon}
                                      Asking for Instruction / Doubtful
                                    </button>
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenClouserForm(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      Closure of Investigations
                                    </button> */}
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenTerminationForm(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      {t("termination_of_case")}
                                    </button>
                                    {/* <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                      }}
                                    >
                                      {actionListIcon}
                                      Decision Not to Finish
                                    </button>
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {}}
                                    >
                                      {actionListIcon}
                                      Institute charge
                                    </button> */}
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenChangeOfVenue(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      {t("change_of_venue")}
                                    </button>
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenArticleOfChange(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      {t("article_of_change")}
                                    </button>
                                    {item?.FinalVerdict &&
                                      item?.FinalVerdict?.length > 0 && (
                                        <button
                                          className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50"
                                          onClick={() => {
                                            setCaseTooltip(undefined);
                                            setSelectedCase(item);
                                            setOpenAppealForm(true);
                                          }}
                                        >
                                          {actionListIcon}
                                          {t("request_appeal")}
                                        </button>
                                      )}
                                  </>
                                )}
                                {isCoordinator && (
                                  <>
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenReassignForm(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      {t("re_assign_prosecutor")}
                                    </button>
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenPanelForm(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      {item?.upload_panel_decision
                                        ? t("panel_decision")
                                        : t("panel")}
                                    </button>
                                    <button
                                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50"
                                      onClick={() => {
                                        setCaseTooltip(undefined);
                                        setSelectedCase(item);
                                        setOpenPerformanceForm(true);
                                      }}
                                    >
                                      {actionListIcon}
                                      {t("performance")}
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <div className="flex justify-center mt-8">
                <CustomPagination
                  pageCount={pageCount}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {openTerminationForm && selectedCase && (
        <TerminationForm
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenTerminationForm(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openChangeOfVenue && selectedCase && (
        <ChangeOfVenueForm
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenChangeOfVenue(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openPerformanceForm && selectedCase && (
        <PerformanceForm
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenPerformanceForm(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openReassignForm && selectedCase && (
        <ReAssignProsecutorForm
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenReassignForm(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openClouserForm && selectedCase && (
        <ClouserForm
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenClouserForm(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openArticleOfChange && selectedCase && (
        <ArticleOfChangeForm
          file={file}
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenArticleOfChange(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openPanelForm && selectedCase && (
        <PanelForm
          caseData={selectedCase}
          onClose={(reload) => {
            setOpenPanelForm(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openSplitCase && selectedCase && (
        <SplitCases
          selectedCase={selectedCase}
          cases={cases}
          onClose={(reload) => {
            setOpenSplitCase(false);
            if (reload) fetchData();
          }}
        />
      )}
      {openAppealForm && selectedCase && (
        <AppealRequestForm
          caseData={selectedCase}
          appealType="Case"
          onClose={() => setOpenAppealForm(false)}
        />
      )}
    </div>
  );
};

export default InstituteOfChargeList;
